import { IonInput } from "@ionic/react";
import { ComponentProps, ReactNode, useMemo } from "react";
import { Control, FieldError, FieldPath, FieldValues, Merge, useController } from "react-hook-form";
import PhoneNumberInput from "react-phone-number-input";

import Error from "@components/fields/Error";
import styles from "@components/fields/PhoneInput.module.css";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import InputLabel from "@components/typography/InputLabel";

import "react-phone-number-input/style.css";

type Props<FormData extends FieldValues> = ComponentProps<typeof IonInput> & {
  control: Control<FormData>;
  error: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  required?: boolean;
};

const PhoneInput = <FormData extends FieldValues>({ control, error, label, name, required }: Props<FormData>) => {
  const {
    field: { onBlur, onChange, value }
  } = useController({ control, name });

  const arrayErrors = useMemo(() => {
    if (error === undefined) {
      return undefined;
    }

    if (!Array.isArray(error)) {
      return [error];
    }
    return error;
  }, [error]);

  return (
    <>
      <div className={styles.wrapperItem}>
        {label && (
          <InputLabel htmlFor={name}>
            {label}
            {required && <RequiredFieldIndicator />}
          </InputLabel>
        )}
        <PhoneNumberInput
          aria-describedby={error ? `${name}-instructions-or-error` : ""}
          aria-invalid={error?.message?.length ? "true" : "false"}
          aria-label={name}
          aria-required={required}
          className={styles.input}
          countrySelectProps={{ styles: { backgroundColor: "red" } }}
          defaultCountry="US"
          flagComponent={flag => <div>{flag.countryName}</div>}
          id={name}
          international
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          placeholder="Enter phone number"
          value={value as string}
        />
      </div>
      {arrayErrors && arrayErrors.length > 0 && (
        <div id={`${name}-instructions-or-error`}>
          {arrayErrors?.map(error => <Error error={error} key={error?.message} />)}
        </div>
      )}
    </>
  );
};

export default PhoneInput;
